import { SearchX } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { type RefObject, useLayoutEffect, useMemo, useReducer } from 'react';
import { useTranslate } from 'ra-core';
import { useGetAllUsers } from '../../hooks/useGetAllUsers';
import { CenteredSpinner } from '../../components/custom/CenteredSpinner';
import { Error as ErrorPage } from '../../components/admin/error';
import { useUserSearch } from './useUserSearch';
import { type UserDto } from '../../hooks/useGetUsers';

export type User = UserDto & { userId: string };

type UserListProps = {
  searchTerm: string;
  renderUser: (user: User, index: number) => React.JSX.Element;
  containerRef: RefObject<HTMLDivElement | null>;
  userIdsFilter?: number[];
  loading?: boolean;
};

/**
 * Renders a list of channel users.
 * @param searchTerm - The search term used to filter the users.
 * @param userIds - The userIds to display on the list. If this prop isn't specified, all users are displayed.
 */
export const UserList = ({
  containerRef,
  searchTerm,
  renderUser,
  userIdsFilter,
}: UserListProps) => {
  const translate = useTranslate();
  const {
    data: growUsers,
    isPending: isPendingUsers,
    isError: isErrorUsers,
    error: errorUsers,
    refetch,
  } = useGetAllUsers();
  const users = useMemo(() => {
    let res = growUsers;
    if (!res) return [];

    if (userIdsFilter) {
      res = res.filter((gu) => userIdsFilter.includes(gu.id));
    }

    return res.map((u) => ({ ...u, userId: `${u.id}` }));
  }, [growUsers, userIdsFilter]);

  const filterUsers = useUserSearch(users);

  const filteredUsers = useMemo(
    () => filterUsers(searchTerm),
    [searchTerm, filterUsers]
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: filteredUsers.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 72,
    overscan: 5,
  });

  const [, forceUpdate] = useReducer((v: number) => v + 1, 0);
  useLayoutEffect(() => {
    // Force component to render twice initially, so that the `containerRef` has a chance to render and be set, and therefore the virtualizer can update accordingly
    forceUpdate();
  }, []);

  if (isPendingUsers) {
    return <CenteredSpinner />;
  } else if (isErrorUsers) {
    return (
      <ErrorPage resetErrorBoundary={() => void refetch()} error={errorUsers} />
    );
  } else if (growUsers.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        {translate('chat.createGroupsModalNoUsersFound')}
      </p>
    );
  }

  if (filteredUsers.length === 0) {
    return (
      <div className="flex h-[55vh] flex-col items-center justify-center">
        <SearchX className="size-24 text-muted-foreground" />
        <p className="p-6 text-center text-muted-foreground">
          {translate('chat.createGroupsModalNoUsersFound')}
        </p>
      </div>
    );
  }

  return (
    <div className="h-[55vh]">
      <div
        className="relative"
        style={{ height: rowVirtualizer.getTotalSize() }}
      >
        {rowVirtualizer.getVirtualItems().map((u) => (
          <div
            key={u.index}
            className="absolute top-0 left-0 w-full"
            style={{ height: u.size, transform: `translateY(${u.start}px)` }}
          >
            {renderUser(filteredUsers[u.index]!, u.index)}
          </div>
        ))}
      </div>
    </div>
  );
};
